import { motion } from "framer-motion";
import heroVideo from "../assests/Text.mp4"; // ✅ Make sure path is correct

export default function TextDataSourcing() {
  return (
    <div className="bg-gray-50 text-gray-900">
      {/* Hero Section */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 px-6">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-4 tracking-tight font-orbitron"
          >
            Building Smarter AI with High-Quality Text Data
          </motion.h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-raleway">
            We source, curate, and structure diverse text datasets to power NLP,
            generative AI, and domain-specific applications.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 tracking-tight font-orbitron">
          Why Text Data Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleway">
          Text is the foundation of natural language processing, powering
          everything from chatbots and search engines to generative AI.
          High-quality, unbiased, and domain-specific text datasets ensure your
          AI understands context, semantics, and human intent accurately.
        </p>
        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-left text-base text-gray-700 leading-relaxed font-raleway">
          <li>✔ Train conversational AI & chatbots</li>
          <li>✔ Enhance search & recommendation systems</li>
          <li>✔ Support machine translation & summarization</li>
          <li>✔ Build domain-specific AI (legal, medical, finance)</li>
        </ul>
      </section>

      {/* Collections Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Our Text Data Collections
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto font-raleway">
          {[
            {
              title: "📚 General Language Corpora",
              desc: "News, blogs, books, and open forums across multiple languages.",
            },
            {
              title: "🏥 Domain-Specific Text",
              desc: "Healthcare, legal, technical, and financial datasets.",
            },
            {
              title: "🌎 Multilingual Data",
              desc: "Text in 50+ languages and dialects for global AI systems.",
            },
            {
              title: "📝 Annotation Services",
              desc: "Sentiment, NER, intent classification, summarization, and more.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="text-xl font-semibold mb-3 font-orbitron">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Our Workflow
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center font-raleway">
          {[
            "Define project requirements",
            "Source & preprocess data ethically",
            "Annotate with expert linguists",
            "Quality assurance & delivery",
          ].map((step, idx) => (
            <div key={idx} className="p-4">
              <div className="text-4xl mb-2">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Applications of Our Text Data
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto font-raleway">
          {[
            {
              title: "🤖 Chatbots & Virtual Assistants",
              desc: "Smarter, context-aware conversations with users.",
            },
            {
              title: "🌐 Machine Translation",
              desc: "Accurate, multilingual communication for global AI.",
            },
            {
              title: "📰 Content Generation",
              desc: "AI-driven summarization, classification & writing support.",
            },
            {
              title: "🏦 Industry-Specific AI",
              desc: "Domain-specific models in healthcare, law, and finance.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="font-semibold mb-2 text-lg font-orbitron">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight font-orbitron">
          Ready to Power Your AI with Smarter Text?
        </h2>
        <p className="text-lg max-w-xl mx-auto font-raleway">
          Let’s build the future of natural language AI together.
        </p>
      </section>
    </div>
  );
}
